import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { reloadWindow } from '@gov-nx/utils/web/utils/reloadWindow';
import { useTabTracking } from './useTabTracking';

export const TabTrackingProvider = ({ children }: PropsWithChildren) => {
	const { removeActiveTab, updateActiveTabCount } = useTabTracking();

	useEffect(() => {
		const onStorageChange = (event: StorageEvent) => {
			if (event.key === 'logoutTimeStamp') {
				reloadWindow();
			}
		};
		window.addEventListener('storage', onStorageChange);
		return () => window.removeEventListener('storage', onStorageChange);
	}, []);

	useEffect(() => {
		const onPageHide = (event: PageTransitionEvent) => {
			// Tab is entering back/forward cache — still alive, do nothing
			if (event.persisted) return;

			removeActiveTab();
		};

		const onPageShow = (event: PageTransitionEvent) => {
			// Re-register tab after bfcache restore — entry may have been pruned
			if (event.persisted) {
				updateActiveTabCount();
			}
		};

		// Refresh heartbeat whenever this tab returns to the foreground so it is
		// never treated as stale by another tab closing while this one is live.
		// This covers two scenarios that pagehide/pageshow alone cannot catch:
		//   • visibilitychange: tab switched away and back (timer-throttled while hidden)
		//   • focus: window re-focused without a full hide/show cycle
		const onVisible = () => {
			if (document.visibilityState === 'visible') {
				updateActiveTabCount();
			}
		};

		const onFocus = () => {
			updateActiveTabCount();
		};

		window.addEventListener('pagehide', onPageHide);
		window.addEventListener('pageshow', onPageShow);
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('focus', onFocus);
		return () => {
			window.removeEventListener('pagehide', onPageHide);
			window.removeEventListener('pageshow', onPageShow);
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('focus', onFocus);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return children;
};
